<script lang="ts" module>
	import { getClients } from "@api/getClients.js";
	import listPublishedFutureCourseTermsQuery from "@api/queries/listPublishedFutureCourseTerms.graphql";
	import { requestAll } from "@api/requestAll.js";
	import homeEspressoImage from "@assets/images/home-espresso-hero.webp?webp&w=600&imagetools";
	import homeFilterCoffeeImage from "@assets/images/home-filter-coffee-hero.webp?webp&w=600&imagetools";
	import heroImage3xl from "@assets/images/sca-foundation-gallery-1.jpg?webp&imagetools";
	import heroImageXs from "@assets/images/sca-foundation-gallery-1.jpg?webp&w=800&quality=95&imagetools";
	import heroImageSm from "@assets/images/sca-foundation-gallery-1.jpg?webp&w=1300&quality=95&imagetools";
	import heroImageLg from "@assets/images/sca-foundation-gallery-1.jpg?webp&w=1600&imagetools";
	import scaFoundationImage from "@assets/images/sca-foundation-hero.jpg?webp&w=600&imagetools";
	import scaIntermediateImage from "@assets/images/sca-intermediate-hero.jpg?webp&w=600&imagetools";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Link from "@components/common/Link.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import BenefitsStrip from "@components/sections/BenefitsStrip.svelte";
	import CourseReviewsSection from "@components/sections/CourseReviewsSection.svelte";
	import CourseTrainersSection from "@components/sections/CourseTrainersSection.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { ItemList as SchemaItemList, WithContext as SchemaWithContext } from "schema-dts";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublicCourseTerm, PublicCourseTermListParams } from "@core/schema/CourseTerm.js";
	import { CourseType } from "@core/schema/CourseType.js";
	import { courseTypeContentMap } from "@core/schema/names/courseTypeContentMap.js";
	import { courseTypeNameMap } from "@core/schema/names/courseTypeNameMap.js";
	import { getCourseTypePath } from "@core/schema/paths/getCourseTypePath.js";
	import { getCoursesAnchor } from "@core/schema/paths/getCoursesAnchor.js";
	import { calculateCourseOrderPrice } from "@core/schema/utils/calculateCourseOrderPrice.js";
	import { idFromAnchor } from "@core/schema/utils/idFromAnchor.js";
	import { isTermFull } from "@core/schema/utils/isTermFull.js";
	import { createSortByDate } from "@core/utils/createSortByDate.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { toDateTimeMonthYearFormat } from "@core/utils/toDateTimeMonthYearFormat.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import translations from "./CoursesListing.translations.json" with { type: "json" };

	const nearestTermsLimit = 2;
	const sortByStartsAt = createSortByDate("startsAt", "asc");
	const heroImageHeight = tw("!h-[max(calc(100vh-8rem),32rem)]");

	const courseTypeImageMap: Record<CourseType, string> = {
		[CourseType.HomeFilterCoffee]: homeFilterCoffeeImage,
		[CourseType.HomeEspresso]: homeEspressoImage,
		[CourseType.SCAFoundation]: scaFoundationImage,
		[CourseType.SCAIntermediate]: scaIntermediateImage,
	};

	/** Courses are listed in groups, so a visitor sees at a glance which of them are meant for them. */
	const courseGroups = ["home", "sca"] as const;
	const courseGroupMap: Record<CourseType, (typeof courseGroups)[number]> = {
		[CourseType.HomeFilterCoffee]: "home",
		[CourseType.HomeEspresso]: "home",
		[CourseType.SCAFoundation]: "sca",
		[CourseType.SCAIntermediate]: "sca",
	};
</script>

<script lang="ts">
	const translate = getTranslate(translations);
	const { tenant, exchangeRatesSetting } = getGlobalTemplateProps();
	const settings = getGlobalSettings();
	const currency = $derived(selectedCurrency.current ?? tenant.defaultCurrency);
	const coursesAnchor = getCoursesAnchor(tenant.locale);

	const courseList: SchemaWithContext<SchemaItemList> = {
		"@context": "https://schema.org",
		"@type": "ItemList",
		name: translate("heading"),
		itemListElement: Object.values(CourseType).map((courseType, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: courseTypeNameMap[courseType][tenant.locale],
			url: `${settings.WEB_URL_OR_LOCAL}${getCourseTypePath(tenant.locale, courseType)}`,
		})),
	};

	async function listNearestTerms(courseType: CourseType): Promise<PublicCourseTerm[]> {
		const { graphQL } = await getClients(settings);
		const terms = await requestAll<PublicCourseTerm, PublicCourseTermListParams>(graphQL, {
			query: listPublishedFutureCourseTermsQuery,
			variables: { courseType },
		});
		return terms.toSorted(sortByStartsAt).slice(0, nearestTermsLimit);
	}

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet courseCard(courseType: CourseType)}
	{@const content = courseTypeContentMap[courseType]}
	{@const detailPath = getCourseTypePath(tenant.locale, courseType)}
	<article class="group/course-card flex flex-col">
		<div class="relative w-full aspect-[3/2] rounded-2xl overflow-hidden bg-fcrlightgray-400">
			<img
				src={courseTypeImageMap[courseType]}
				alt=""
				width={600}
				height={400}
				loading="lazy"
				class="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover/course-card:scale-105"
			/>
			<a href={detailPath} class="absolute inset-0" tabindex={-1} aria-hidden="true"></a>
		</div>

		<Heading
			level={3}
			text="[{detailPath}][[{courseTypeNameMap[courseType][tenant.locale]}]]"
			class={[
				"font-antipol text-fcrdarkgray text-base md:text-1.5xl mt-4 md:mt-6 transition-colors",
				"group-hover/course-card:text-fcrbrown group-hover/course-card:underline [&>a]:no-underline",
			]}
		/>
		<div class="mt-1 md:mt-2 text-fcrdarkgray text-sm/normal md:text-base">
			<Formatted
				text={formatPrice(
					calculateCourseOrderPrice(courseType, currency, exchangeRatesSetting?.exchangeRates ?? []).withTax,
					currency,
					tenant.locale,
					false,
				)}
			/>
		</div>
		<p class="mt-2 text-sm/normal sm:text-base text-fcrgray line-clamp-3">
			<Formatted text={content.shortDescription[tenant.locale]} />
		</p>

		{#await listNearestTerms(courseType)}
			<div class="mt-4 flex flex-col gap-2">
				{#each { length: nearestTermsLimit } as _unused, index (index)}
					<div class="h-13 rounded-lg bg-fcrlightgray-300 animate-pulse"></div>
				{/each}
			</div>
		{:then terms}
			{#if terms.length > 0}
				<ul class="mt-4 border-t border-fcrlightgray-300">
					{#each terms as term (term.id)}
						{@const seatsLeft = term.capacity - term.participantsCount}
						<li class="border-b border-fcrlightgray-300">
							<a
								href="{detailPath}?term={term.id}"
								class="flex items-center justify-between gap-3 py-3 transition-colors hover:text-fcrbrown"
							>
								<span class="flex flex-col min-w-0">
									<span class="text-sm/normal md:text-base font-medium">
										{toDateTimeMonthYearFormat(term.startsAt, tenant.locale)}
									</span>
									<span class="text-sm/normal text-fcrgray truncate">{term.placeName}</span>
								</span>
								<span
									class={[
										"shrink-0 rounded-full border-[1.5px] px-3 py-0 uppercase font-antipol text-2.5 leading-[1.15rem]",
										isTermFull(term) ? "border-fcrerror/40 text-fcrerror" : "border-fcrlightgray-300 text-fcrdarkgray",
									]}
								>
									{isTermFull(term) ? translate("full") : translate("seatsLeft", { count: seatsLeft })}
								</span>
							</a>
						</li>
					{/each}
				</ul>
			{:else}
				<p class="mt-4 text-sm/normal sm:text-base text-fcrgray">
					<Formatted text={translate("noTerms")} />
				</p>
			{/if}
		{:catch}
			<p class="mt-4 text-sm/normal sm:text-base text-fcrgray">
				<Formatted text={translate("noTerms")} />
			</p>
		{/await}

		<div class="mt-auto pt-6 md:pt-8">
			<Link href={detailPath} variant={ButtonVariant.Outlined} size={ButtonSize.Medium} class="w-full">
				<Formatted text={translate("viewCourse")} />
			</Link>
		</div>
	</article>
{/snippet}

<Layout transparentHeader ogImage={heroImageSm}>
	<StructuredData data={courseList} />

	<ImageSection
		lcp
		darkOverlay="!bg-black/40"
		class={heroImageHeight}
		innerClass="!grid-rows-[1fr_auto] [&>*]:min-h-0"
		sources={{
			default: heroImageXs,
			">sm": heroImageSm,
			">lg": heroImageLg,
			">3xl": heroImage3xl,
		}}
	>
		{#snippet heading()}
			<div class="flex h-full flex-col items-center justify-center gap-4 md:gap-6">
				<Heading level={1} text={translate("heroTitle")} class="heading-1 text-white" />
				<p class="text-white/80 max-w-140 text-sm/normal sm:text-lg/normal">
					<Formatted text={translate("heroLead")} />
				</p>
			</div>
		{/snippet}
		{#snippet actions()}
			<Link href={coursesAnchor} size={ButtonSize.Medium} variant={ButtonVariant.White}>
				<Formatted text={translate("heroCta")} />
			</Link>
		{/snippet}
	</ImageSection>

	<BenefitsStrip items={[translate("benefitPractice"), translate("benefitPlaces"), translate("benefitTeam")]} />

	<section class="py-14 sm:py-17 md:py-20 text-center container-6">
		<p class="font-antipol font-medium text-8/tighter text-fcrdarkgray mb-8">
			<Formatted text={translate("introTitle")} />
		</p>
		<div class="flex flex-col gap-4 text-sm/tight sm:text-lg/normal">
			<p><Formatted text={translate("introParagraphJourney")} /></p>
			<p><Formatted text={translate("introParagraphPractice")} /></p>
		</div>
	</section>

	<div id={idFromAnchor(coursesAnchor)} class="flex flex-col gap-16 md:gap-24 scroll-mt-24">
		{#each courseGroups as group (group)}
			<section class="flex flex-col">
				<div class="flex flex-col gap-2 text-center max-w-160 mx-auto mb-8 md:mb-12">
					<Heading level={2} text={translate(`${group}Heading`)} class="heading-2" />
					<p class="text-sm/normal sm:text-base text-fcrgray">
						<Formatted text={translate(`${group}Description`)} />
					</p>
				</div>
				<div class="grid gap-x-4 gap-y-14 md:grid-cols-2 md:gap-x-8 md:gap-y-18 w-full max-w-168 mx-auto">
					{#each Object.values(CourseType).filter((courseType) => courseGroupMap[courseType] === group) as courseType (courseType)}
						{@render courseCard(courseType)}
					{/each}
				</div>
			</section>
		{/each}
	</div>

	<CourseTrainersSection label={translate("trainersLabel")} title={translate("trainersTitle")} />

	<CourseReviewsSection>
		<Link href={coursesAnchor} size={ButtonSize.Medium} variant={ButtonVariant.Dark}>
			<Formatted text={translate("heroCta")} />
		</Link>
	</CourseReviewsSection>
</Layout>
